import React, {useContext, useState} from "react";
import {
    Avatar,
    Button,
    Stack,
    Toolbar,
    Typography,
    AppBar,
    IconButton,
    Menu, Box, MenuItem, useTheme, ListItemIcon, ListItemText
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {Link, NavLink} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {ConstructionIcon, Logout01Icon, Menu01Icon, UserIcon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../../context/SignInContext";
import {grey} from "@mui/material/colors";
import {HeaderDrawer} from "./HeaderDrawer";
import {WebSettingsContext} from "../../../context/WebSettingsContext";


const navLinkSx = {
    borderRadius: "20px",
    textTransform: "none",
    fontSize: 13,
    fontWeight: 500,
    color: "text.secondary",
    px: 2,
    py: 0.75,
    minWidth: 0,
    transition: "box-shadow 0.15s ease, background-color 0.15s ease",
    "&.active": {
        color: "primary.main",
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        boxShadow: (theme) => `0 1px 6px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
    "&:hover": {
        bgcolor: "action.hover",
        boxShadow: "0 1px 6px rgba(0,0,0,0.15)",
    },
};

export const Header = () => {
    const {isAuth, currentUser, onLogout, openModal} = useContext(SignInContext);
    const [openDrawer, setOpenDrawer] = useState(false);

    const theme = useTheme();

    const isAdmin = currentUser?.roles?.includes("admin")


    return (
        <>
            <HeaderDrawer
                open={openDrawer}
                handleClose={() => setOpenDrawer(false)}
                isAuth={isAuth}
                isAdmin={isAdmin}
                currentUser={currentUser}
                onLogout={onLogout}
                openModal={openModal}
            />

            <AppBar position="static"
                    sx={{
                        bgcolor: "transparent",
                        boxShadow: 0,
                        zIndex: 100,
                        borderBottom: 0,
                        minHeight: "48px !important",
                        borderColor: "divider"
                    }}
            >
                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        display: {xs: 'none', md: 'flex'},
                        zIndex: 100,
                        py: 1, px: 2,
                        minHeight: "48px !important",
                    }}
                >
                    <Stack direction={"row"} sx={{width: "100%"}} gap={2} justifyContent={"space-between"}
                           alignItems={"center"}>
                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            <Button
                                component={Link}
                                to={"/projects"}
                                variant={"ghost"}
                                sx={{flexDirection: "row", color: "dark.main"}}
                                startIcon={<img src={"/logo.svg"} alt={"logo"} width={30}/>}
                            >
                                <Typography fontWeight={700}>BlockFlow</Typography>
                            </Button>
                        </Stack>

                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            {isAdmin && <>
                                <Button
                                    component={NavLink}
                                    to={"/projects"}
                                    end
                                    sx={navLinkSx}
                                >
                                    Projects
                                </Button>
                                <Button
                                    component={NavLink}
                                    to={"/organizations"}
                                    sx={navLinkSx}
                                >
                                    Organizations
                                </Button>
                                <Button
                                    component={NavLink}
                                    to={"/ai-models"}
                                    sx={navLinkSx}
                                >
                                    AI models
                                </Button>
                                <Button
                                    component={NavLink}
                                    to={"/users"}
                                    sx={navLinkSx}
                                >
                                    Users
                                </Button>
                            </>}

                            {
                                isAuth ?
                                    <UserAvatar currentUser={currentUser} onLogout={onLogout} theme={theme}/>
                                    :
                                    <Button
                                        onClick={() => openModal()}
                                        sx={{
                                            bgcolor: "transparent",
                                            borderRadius: 0,
                                            textTransform: "capitalize",
                                            fontSize: 14,
                                            px: 2,
                                            fontWeight: "bold",
                                            color: '#4a4a4a',
                                            borderColor: "#4a4a4a",
                                            border: 1,
                                            "&:hover": {
                                                color: "#FFF",
                                                bgcolor: "#4a4a4a"
                                            }
                                        }}
                                    >
                                        Sign in
                                    </Button>
                            }

                        </Stack>
                    </Stack>
                </Toolbar>

                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        my: 1,
                        display: {xs: 'flex', md: "none"}
                    }}
                >
                    <Stack direction={"row"} sx={{width: "100%"}} gap={2} justifyContent={"space-between"}
                           alignItems={"center"}>
                        <Button
                            component={Link}
                            to={"/projects"}
                            variant={"ghost"}
                            sx={{color: "#252525", flexDirection: "column"}}
                        >
                            <img src={"/logo.svg"} alt={"logo"} width={50}/>
                            {/*<Typography fontSize={13} fontColor={"#252525"} fontWeight={"bold"}>BlockFlow</Typography>*/}
                        </Button>

                        <IconButton onClick={() => setOpenDrawer(true)}>
                            <HugeiconsIcon icon={Menu01Icon}/>
                        </IconButton>
                    </Stack>
                </Toolbar>
            </AppBar>
        </>
    )
}

export const UserAvatar = ({currentUser, onLogout}) => {
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const isAdmin = currentUser?.roles?.includes("admin")
    const {settings, onUnderMaintenance} = useContext(WebSettingsContext);

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const logoutHandle = async () => {
        try {
            await onLogout();
            handleCloseUserMenu();
        } catch (err) {
            console.error(err);
        }
    }

    return (
        <Box sx={{flexGrow: 0}}>
            <Button
                onClick={handleOpenUserMenu}
                size={"small"}
                sx={{
                    borderRadius: "8px !important",
                    minWidth: "0 !important",
                }}
            >
                <Avatar sx={{bgcolor: grey[200]}}>
                    <HugeiconsIcon icon={UserIcon} color={grey[700]} size={20}/>
                </Avatar>
            </Button>
            <Menu
                sx={{p: 0, borderRadius: 0, minWidth: "280px"}}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                MenuListProps={{sx: {borderRadius: 4, minWidth: "200px"}}}
                PaperProps={{sx: {borderRadius: 4, minWidth: "200px"}}}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <Stack direction={"row"} gap={1} alignItems={"center"}
                       sx={{px: 2, py: 1, borderBottom: 1, borderColor: "divider"}}>
                    <Avatar sx={{width: 35, height: 35, bgcolor: grey[200]}}>
                        <HugeiconsIcon icon={UserIcon} color={grey[700]} size={20}/>
                    </Avatar>
                    <Typography fontWeight={"bold"}>{currentUser?.name} {currentUser?.lastname}</Typography>
                </Stack>
                {
                    isAdmin &&
                    <MenuItem onClick={onUnderMaintenance}
                              color={settings?.underMaintenance ? "error" : "primary"}
                              sx={{borderBottom: 1, borderColor: "divider", px: 3, py: 1, fontSize: 16}}>
                        <ListItemIcon>
                            <HugeiconsIcon icon={ConstructionIcon}/>
                        </ListItemIcon>
                        <ListItemText primary={"Under Maintenance"}/>
                    </MenuItem>
                }
                <MenuItem onClick={logoutHandle}
                          sx={{px: 3, py: 1, fontSize: 16}}>
                    <ListItemIcon>
                        <HugeiconsIcon icon={Logout01Icon}/>
                    </ListItemIcon>
                    <ListItemText primary={"Logout"}/>
                </MenuItem>
            </Menu>
        </Box>
    )
}
